import { skipToken, type TypedDocumentNode, useQuery } from '@base-app/web2'

export type RecordStatus = 'new' | 'loading' | 'ready' | 'missing' | 'error'

/**
 * Loads one record for a form by id: from the Apollo cache when a list already has it
 * (see typePolicies in lib/apollo.ts), otherwise from the API. Without an id the form is
 * creating a record, so nothing loads.
 */
export function useRecordById<TData, TRecord>(
  query: TypedDocumentNode<TData, { id: string }>,
  id: string | undefined,
  select: (data: TData) => TRecord | null | undefined,
): { record: TRecord | undefined; status: RecordStatus } {
  const { data, error } = useQuery(query, id ? { variables: { id } } : skipToken)
  if (!id) {
    return { record: undefined, status: 'new' }
  }
  if (error) {
    return { record: undefined, status: 'error' }
  }
  if (!data) {
    return { record: undefined, status: 'loading' }
  }
  const record = select(data as TData)
  return record ? { record, status: 'ready' } : { record: undefined, status: 'missing' }
}
